import { useState, useCallback, useEffect, useRef } from 'react'
import type { ApolloClient } from '@apollo/client'
import type { User, BookShelf } from 'api/data.types'
import { MainScreenSuggestionsQuery } from 'api/queries/reader'
import type { Module } from 'api/data.types'
import { post_event } from 'utils/analytics'
import { allBookshelvesQuery } from 'api/queries'

const useSuggestions = (
  { query }: Pick<ApolloClient<object>, 'query'>,
  contentFilter: Module,
  isStudentView: boolean,
  degree: string
) => {
  const [shelves, setShelves] = useState<BookShelf[]>([])
  const [nextPage, setNextPage] = useState<number | null>(1)
  const isQuering = useRef(false)
  const shelfsPerPage = 5

  useEffect(() => {
    setShelves([])
    setNextPage(1)
  }, [degree])

  const querySuggestions = async (page: number) => {
    const res = await query<{
      me: User
    }>({
      query: MainScreenSuggestionsQuery,
      variables: {
        page: page,
        perPage: shelfsPerPage,
        contentFilter,
        limit: 20
      }
    })

    return res.data.me.reader.mainScreenSuggestionsv3
  }

  const queryAsStudent = async (page: number) => {
    const res = await query<{
      listExistingBookshelf: BookShelf[]
    }>({
      query: allBookshelvesQuery,
      variables: {
        contentFilter,
        degreeRange: degree,
        page: page,
        isHighlight: false,
        perPage: shelfsPerPage,
        acervoType: null,
        active: true,
        type: 'bookshelf'
      }
    })

    return res.data.listExistingBookshelf
  }

  const getShelves = useCallback(async () => {
    if (isQuering.current) {
      return
    }
    isQuering.current = true

    try {
      const suggestions = isStudentView
        ? await queryAsStudent(nextPage)
        : await querySuggestions(nextPage)

      if (suggestions.length) {
        post_event(
          `ViuSugestoesPrateleira/${contentFilter}`,
          `${nextPage}_prateleira_paginacao`
        )

        setShelves(currentState => [...currentState, ...suggestions])
        setNextPage(currentState => currentState + 1)
      } else {
        post_event(
          `ViuTodasSugestoesPrateleira/${contentFilter}`,
          `fim_sugestao_prateleira`
        )
        setNextPage(null)
      }
    } catch (err) {
      setNextPage(null)
      console.log(`Oops, aconteceu um erro: ${err.message}`)
    } finally {
      isQuering.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextPage, degree])

  useEffect(() => {
    if (isQuering.current || nextPage !== 1 || shelves.length) {
      return
    }
    getShelves()
  }, [nextPage, shelves.length, getShelves])

  const hasMoreShelves = Boolean(nextPage)

  return { shelves, getShelves, hasMoreShelves }
}

export { useSuggestions }
